import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Trash2 } from 'lucide-react';

export const PromoCodesTab = ({
  promoCodes, showForm, setShowForm, newPromo, setNewPromo, onCreate, onDelete
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91]">Promo Codes</h3>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#0B3D91]"
          data-testid="create-promo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promo Code
        </Button>
      </div>
      {showForm && (
        <form onSubmit={onCreate} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <h4 className="font-medium text-slate-900 mb-4">New Promo Code</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-600 mb-1">Code</label>
              <Input
                placeholder="e.g., LAUNCH50"
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                required
                data-testid="promo-code-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Discount %</label>
              <Input
                type="number"
                min="1"
                max="100"
                value={newPromo.discount_percent}
                onChange={(e) => setNewPromo({ ...newPromo, discount_percent: parseInt(e.target.value) })}
                required
              />
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Valid From</label>
              <Input
                type="date"
                value={newPromo.valid_from}
                onChange={(e) => setNewPromo({ ...newPromo, valid_from: e.target.value })}
                data-testid="promo-valid-from"
              />
              <p className="text-xs text-slate-400 mt-0.5">Leave empty for immediate activation</p>
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Valid To</label>
              <Input
                type="date"
                value={newPromo.valid_to}
                onChange={(e) => setNewPromo({ ...newPromo, valid_to: e.target.value })}
                data-testid="promo-valid-to"
              />
              <p className="text-xs text-slate-400 mt-0.5">Leave empty for no expiry</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-slate-500">
              Applies to Starter and Growth plans. {newPromo.discount_percent}% off.
            </p>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              Create Code
            </Button>
          </div>
        </form>
      )}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Discount</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Valid Period</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Uses</th>
                <th className="text-left p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-right p-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {promoCodes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-slate-400">
                    No promo codes yet. Create your first one!
                  </td>
                </tr>
              ) : promoCodes.map((promo) => {
                const status = promo.effective_status || (promo.active ? 'active' : 'deactivated');
                const statusStyles = {
                  active: 'bg-emerald-100 text-emerald-700',
                  scheduled: 'bg-blue-100 text-blue-700',
                  expired: 'bg-slate-100 text-slate-500',
                  deactivated: 'bg-red-100 text-red-600',
                  exhausted: 'bg-amber-100 text-amber-700'
                };
                return (
                  <tr key={promo.code} className="hover:bg-slate-50" data-testid={`promo-row-${promo.code}`}>
                    <td className="p-4">
                      <code className="bg-slate-100 px-2 py-1 rounded font-mono text-sm">{promo.code}</code>
                    </td>
                    <td className="p-4">
                      <span className="text-emerald-600 font-medium">{promo.discount_percent}% off</span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      <div>
                        {promo.valid_from ? (
                          <span>{new Date(promo.valid_from).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-slate-400">No start</span>
                        )}
                        <span className="mx-1 text-slate-300">-</span>
                        {promo.valid_to ? (
                          <span>{new Date(promo.valid_to).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-slate-400">No end</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      {promo.times_used} / {promo.max_uses === -1 ? '\u221e' : promo.max_uses}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.active}`}
                        data-testid={`promo-status-${promo.code}`}>
                        {status}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {status !== 'deactivated' && (
                        <button
                          onClick={() => onDelete(promo.code)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500"
                          title="Deactivate"
                          data-testid={`deactivate-promo-${promo.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
